import { AfterViewInit, ViewChild, ElementRef, EventEmitter, Component, Input, NgModule, Output, OnInit, ChangeDetectorRef } from "@angular/core";
import { debounceTime, filter, takeUntil, Subject } from "rxjs";
import { maxZIndex, WRxBase, WUniversalModule } from '@wipo/w-angular/shared';
import { CommonModule } from "@angular/common";
import { WDrawerComponent, WDrawerModule } from "@wipo/w-angular/w-drawer";
import { WClickOutsideModule } from "@wipo/w-angular/directives";
import { FFacetModule } from "@wipo/w-angular/f-facet";

const OFFSET_DEFAULT = 50;

@Component({
    selector: 'w-dropdown',
    template: `
    <ng-template #dropdown><ng-content></ng-content></ng-template>
    <template #dropdownTemplate><ng-container *ngTemplateOutlet="dropdown"></ng-container></template>
    <span [class.b-pointer__arrow]="!hideArrow" *ngIf="open" tabindex="-1" #ptr>
       <w-drawer #drawer *ngIf="hasDrawer && open" compact w-click-outside 
            [close-button]="_close" (wClose)="close()"
            [style.width]="width" [clickInsiders]="insideElements"
            (clickOutside)="close()" [isPopup]="true" [isFilter]="true">
            <ng-container *ngTemplateOutlet="dropdown"></ng-container>
        </w-drawer>
    </span>
    `,
    host: {
        class: 'b-dropdown',
        '[class.b-pointer]': 'open',
        '[style.zIndex]': 'maxZIndex'
    },
    standalone: false
})
/*
@id w-dropdown
@name w-dropdown
@status release
@description handles dropdown in other components - buttons, table commands, etc. toogle visibility from by event or by using "open" value
*/
export class WDropdownComponent extends WRxBase implements OnInit, AfterViewInit {
    @ViewChild('dropdownTemplate') dropdownTemplate!: ElementRef<HTMLTemplateElement>;
    @ViewChild('ptr') ptrSpan!: ElementRef<HTMLSpanElement>;
    @ViewChild('drawer') drawer?: WDrawerComponent;
    @Input('close-button') _close?: string; // if attribute present - show close icon/button

    @Input() width?: string; // prefered dropdown width (any valid string value including calc)
    @Input() hideArrow?: boolean; // do not show dropdown arrow (if external component like table command link generates one itself)

    /*  */
    @Input() wToggle?: EventEmitter<any>; // (optional) if provided triggers open/close of dropdown
    _pointer = false;
    /* toggle dropdown */
    @Input() set open(value: boolean) /* open/close dropdown */ {
        // skip any click-outside action, when pointer changed from external button
        setTimeout(() => {
            const ptr = this._pointer;
            this._pointer = value;
            // emit update when pointer changed
            if (ptr !== this.open) {
                this.openChange.emit(this.open);
                this.changeDetectorRef.detectChanges();
            }
        })
    };
    get open() {
        return this._pointer;
    }
    /* returns updated "open" value */
    @Output() openChange = new EventEmitter<boolean>();

    insideElements!: HTMLElement[];
    get el(): HTMLElement {
        return this.elRef.nativeElement;
    }

    hasDrawer: boolean = false;
    private offset = 0;
    reposition$ = new Subject<void>();
    maxZIndex = 0; // check for z-index when opening

    constructor(public elRef: ElementRef, private changeDetectorRef: ChangeDetectorRef) {
        super();
    }
    ngOnInit(): void {
        if (this.isBrowser) {
            this.reposition$.pipe(takeUntil(this.destruction), debounceTime(50)).subscribe(() => {
                this.reposition();
            })
        }
        this.insideElements = [this.el, this.el.parentElement].filter(e => !!e) as HTMLElement[];
        this.openChange.subscribe(open => {
            this.adjustZindex(!open);
            if (!open) {
                this.offset = 0;
                this.el.style.removeProperty('--dropdown-start');
                this.el.style.removeProperty('--arrow-start');
            }
            this.reposition$.next();
        })
    }

    close() {
        this.toggleState(false);
        if (this.wToggle) {
            this.wToggle.emit(this.el);
        }
    }
    private toggleState(force?: boolean) {
        this.ptrSpan?.nativeElement.focus();
        this.open = force === undefined ? !this.open : force;
    }

    ngAfterViewInit(): void {
        if (this.isBrowser) {
            setTimeout(() => {
                this.hasDrawer = this.dropdownTemplate.nativeElement.content.childElementCount > 0;
                // listen for action
                this.wToggle?.pipe(
                    filter(() => this.hasDrawer),
                    takeUntil(this.destruction),
                    // ignore clicks coming from a child element
                    filter(source => !isChildOrSelf(this.el, source))
                )
                    .subscribe((a) => {
                        this.toggleState();
                        // adjust position
                    });
                this.fromEvent(window, 'resize').subscribe(() => this.reposition$.next());
            }, 100)
        }
    }
    private adjustZindex(closing: boolean) {
        // adjust zIndex
        const zIndexMax = maxZIndex();
        if (closing) {
            // we are closing - reset z-index
            this.maxZIndex = 0;
        } else {
            // we are toggling open, use z-index
            this.maxZIndex = zIndexMax + 1;
        }
    }
    private reposition() {
        const winwidth = document.documentElement.clientWidth;
        setTimeout(() => {
            const drawerElement = this.drawer?.elRef.nativeElement;
            if (drawerElement) {
                const drawerRectangle = drawerElement.getBoundingClientRect();
                const parentWidth = this.el.parentElement?.getBoundingClientRect().width ?? OFFSET_DEFAULT * 2;
                const offsetDefault = Math.round(parentWidth / 2);

                const rtl = getComputedStyle(drawerElement).direction === 'rtl';
                const deltaStart = (rtl ? winwidth - drawerRectangle.right : drawerRectangle.left);
                const deltaEnd = (rtl ? drawerRectangle.left : winwidth - drawerRectangle.right) - 16; // space to side (scrollbar etc)
                // if far from border use parent center offset, else fit within margins
                let ddl = Math.min(deltaStart + this.offset, Math.max(offsetDefault, this.offset - deltaEnd));
                ddl = Math.max(0, ddl);

                // if drawer overflows start edge, adjust its inline margin to prevent clipping
                if (deltaStart < 0) {
                    drawerElement.style.marginInlineStart = `calc(-1.5rem - ${deltaStart}px)`;
                } else {
                    drawerElement.style.marginInlineStart = '';
                }

                // if drawer overflows end edge, reduce its negative inline-end margin
                const endOverflow = rtl
                    ? drawerRectangle.left
                    : winwidth - drawerRectangle.right;
                if (endOverflow < 0) {
                    drawerElement.style.marginInlineEnd = `calc(-1.5rem - ${endOverflow}px)`;
                } else {
                    drawerElement.style.marginInlineEnd = '';
                }

                this.el.style.setProperty('--dropdown-start', `-${ddl}px`);
                this.offset = ddl;

                // position arrow to point at parent button center
                const parentRect = this.el.parentElement?.getBoundingClientRect();
                const dropdownRect = this.el.getBoundingClientRect();
                if (parentRect && dropdownRect.width > 0) {
                    const parentCenter = parentRect.left + parentRect.width / 2;
                    const arrowLeft = parentCenter - dropdownRect.left - 13; // 13px = half of arrow
                    this.el.style.setProperty('--arrow-start', `${arrowLeft}px`);
                }
            }
        }, 30);
    }
}
const isChildOrSelf = (el: HTMLElement, otherElement: HTMLElement): boolean => {
    return el === otherElement || (!!otherElement?.parentElement && isChildOrSelf(el, otherElement.parentElement));
}
@NgModule({
    imports: [CommonModule, WDrawerModule, WClickOutsideModule, WUniversalModule, FFacetModule],
    exports: [WDropdownComponent],
    declarations: [WDropdownComponent]
})
export class WDropdownModule { }